/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './fans-overview.scss';

/* eslint-disable import/no-unresolved, import/default */

import pageTemplate from './fans-overview.tpl.html';

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.fansOverview', [])
    .directive('agFansOverview', FansOverview)
    .name;

/*@ngInject*/
function FansOverview() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            silo: '=',
            aerationType: '='
        },
        controller: FansController,
        controllerAs: 'vm',
        templateUrl: pageTemplate
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function FansController($scope, $filter, $translate, $q, $log, types, agTypes, agUtils, siloService, unitService, userService ,timeService) {

    let vm = this;

    vm.agTypes = agTypes;
    vm.loading = false;
    vm.showData = userService.getAuthority() === 'TENANT_ADMIN' || userService.getAuthority() === 'CUSTOMER_USER';
    vm.resolveIcon = resolveIcon;
    vm.resolveColor = resolveColor;
    vm.flotSettings = {
        shadowSize: 0,
        yaxis: {
            showLabels: false,
            min: 0,
            max: 1.25
        },
        xaxis: {
            showLabels: false
        },
        grid: {
            outlineWidth: 0,
            margin: 0,
            minBorderMargin: 0,
            tickColor: 'rgba(255, 255, 255, 0)' // Workaround to hide grid lines
        }
    };

    vm.flotKeySettings = {
        lineWidth: 1,
        axisTicksFormatter: `if (value >= 0 && value <=1 ) { 
                                return value < 1 ? '${$translate.instant('ag.aeration.off')+''}' : '${$translate.instant('ag.aeration.on')+''}'; 
                             } else {
                                return '';
                             };`,
        tooltipValueFormatter: `return value == 1 ? '${$translate.instant('ag.aeration.on')+''}' : '${$translate.instant('ag.aeration.off')+''}';`
    };

    vm.latestValuesSubscriptionOptions = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError,
            dataLoading: function(subscription) {
                vm.loading = subscription.loadingData;
            }
        },
        useDashboardTimewindow: false
    };

    vm.alarmsSubscriptionOptions = {
        callbacks: {
            onDataUpdated: onAlarmsUpdated
        },
        timeWindowConfig: timeService.defaultTimewindow(),
        useDashboardTimewindow: false,
        alarmsPollingInterval: 5000,
        alarmSearchStatus: types.alarmSearchStatus.active
    };

    vm.alarmsSubscriptionOptions.timeWindowConfig.aggregation.type = types.aggregation.none.value;
    vm.alarmsSubscriptionOptions.timeWindowConfig.realtime.timewindowMs = Date.now() - (60 * 60 * 1000);

    function reset() {
        if (vm.latestValuesSubscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.latestValuesSubscription.id);
        }

        vm.silo.connectedSystems.forEach((aeration) => {
            if (vm[aeration.id.id]?.alarmSubscription) {
                vm.ctx.subscriptionApi.removeSubscription(vm[aeration.id.id]?.alarmSubscription.id);
            }
        });
    }

    $scope.$on('$destroy', function() {
        reset();
    });

    if (vm.showData && vm.silo) {
        reset();
        loadAerationInfo();
    }

    function onAlarmsUpdated() {
        vm.silo.connectedSystems.forEach((aeration) => {
            vm[aeration.id.id].hasAlarm = false;
            if (vm[aeration.id.id]?.alarmSubscription) {
                const allAlarms = vm[aeration.id.id]?.alarmSubscription.alarms ?? [];
                allAlarms.forEach(alarmInfo => {
                  if (alarmInfo.type === agTypes.alarmType.AERATION_ALARM || alarmInfo.type === agTypes.alarmType.AERATION_STATE_ALARM) {
                      if (alarmInfo.severity === 'CRITICAL' && alarmInfo.status === types.alarmStatus.activeUnack) {
                          vm[aeration.id.id].hasAlarm = true;
                      }
                  }
                })
            }
        });
        $scope.$digest();
    }

    function loadAerationInfo() {
        vm.loading = true;

        vm.aerationStateHistorySubscriptionsInfo = [];
        let alarmsSubscriptionInfo = {};
        const latestValuesSubscriptionsInfo = [];
        vm.silo.connectedSystems.forEach((aeration) => {

            if (aeration.aerationType === vm.aerationType) {
                vm[aeration.id.id] = {
                    currentState: '',
                    hasAlarm: false,
                    alarmSubscription: null
                };

                latestValuesSubscriptionsInfo.push({
                    type: types.datasourceType.entity,
                    entityType: types.entityType.asset,
                    entityId: aeration.id.id,
                    entityName: aeration.name,
                    timeseries: [
                        {name: vm.agTypes.telemetry.aeration.state}
                    ]
                });

                vm.aerationStateHistorySubscriptionsInfo.push({
                    type: types.datasourceType.entity,
                    entityType: types.entityType.asset,
                    entityId: aeration.id.id,
                    entityName: aeration.name,
                    timeseries: [
                        {
                            name: agTypes.telemetry.aeration.state,
                            label: aeration.name,
                            color: agTypes.colors.normalColor,
                            postFuncBody: "return value == 'on' ? 1 : 0;"
                        }
                    ]
                });

                alarmsSubscriptionInfo = {
                    type: types.datasourceType.entity,
                    entityType: types.entityType.asset,
                    entityId: aeration.id.id,
                    entityName: aeration.name,
                    alarmFields: [
                        {name: types.alarmFields.createdTime.keyName},
                        {name: types.alarmFields.originator.keyName},
                        {name: types.alarmFields.type.keyName},
                        {name: types.alarmFields.severity.keyName},
                        {name: types.alarmFields.status.keyName}
                    ]
                };

                vm.ctx.subscriptionApi.createSubscriptionFromInfo(
                    types.widgetType.alarm.value, alarmsSubscriptionInfo, vm.alarmsSubscriptionOptions, false, false).then(
                    function(subscription) {
                        vm[aeration.id.id].alarmSubscription = subscription;
                        subscription.subscribe();
                    }
                );
            }
        });

        vm.ctx.subscriptionApi.createSubscriptionFromInfo(
            types.widgetType.latest.value, latestValuesSubscriptionsInfo, vm.latestValuesSubscriptionOptions, false, true).then(
            function(subscription) {
                vm.latestValuesSubscription = subscription;
            }
        );

        vm.loading = false;
    }

    function onDataUpdated(subscription, apply) {
        var data = subscription.data;
        for (var i=0;i<data.length;i++) {
            var datasourceData = data[i];
            var val = getValue(datasourceData);
            switch (datasourceData.dataKey.name) {
                case vm.agTypes.telemetry.aeration.state:
                    vm[datasourceData.datasource.entityId].currentState = val;
                    break;
            }
        }
        if (apply) {
            $scope.$digest();
        }
    }

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }

    function getValue(keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][1])) {
            return keyData.data[0][1];
        } else {
            return '';
        }
    }

    function resolveIcon(aerationType) {
        return aerationType === agTypes.aerationType.alarm ? "ag:aeration_alarm" : "ag:aeration";
    }

    function resolveColor(aeration) {
        return aeration.hasAlarm ? agTypes.colors.errorColor : agTypes.colors.normalColor;
    }
}
